/**
 * Project search results (Issue #2750).
 *
 * The search field in the project top app bar asks the server for passages across the whole
 * project (`SearchPlanPassages`: keyword and meaning search in one call), or for exact matches
 * (`SearchPlanText`) when the project has no passage index. This module decides how the results
 * panel orders, groups and marks up those hits, what it says about the index, and how a hit's box
 * becomes a Preview highlight.
 *
 * It is pure (no Angular), like `highlight-regions.ts`, so it can be tested on its own.
 */
import { HighlightBox } from './highlight-regions';

/** How a result matched the query. */
export type ProjectSearchMatch = 'words-and-meaning' | 'words' | 'meaning' | 'exact';

/**
 * Where a hit's words were read from: the PDF's own text layer, text recognition (OCR) on a
 * scanned sheet, `sheet` for a passage, which may hold either, or `object` for a plan object found
 * in the sheet's drawings (Issue #3212).
 */
export type ProjectSearchWordSource = 'text-layer' | 'ocr' | 'sheet' | 'object';

/**
 * The plan object a result stands for (Issue #3212): a room, a unit, a drawing, a fixture, a note
 * block, a schedule, a detail or a sheet, found in the sheet's drawings. Its outline is the hit's
 * box, so opening it highlights the object rather than a line of text.
 */
export interface ProjectSearchEntity {
  id: string;
  /** `room`, `unit`, `drawing`, `fixture`, `note-block`, `schedule`, `detail` or `sheet`. */
  kind: string;
  /** Its name, such as "Bath 1". */
  title: string;
  /** Where it sits, from its sheet down to it, such as "A1.7 UNIT TYPE D FLOOR PLAN › Unit Type D › Bath 1". */
  path: string;
  /** The name of what holds it, such as "Unit Type D"; empty for a sheet. */
  parentTitle: string;
}

/** One result: a passage (passage search) or an exact match with its context (exact search). */
export interface ProjectSearchHit {
  fileId: string;
  pageNumber: number;
  /** Sheet identifier, such as "A-201". Empty when the plan data has none. */
  sheetId: string;
  /** The passage's lines, or the context around an exact match. */
  text: string;
  /** The words that matched, for an exact match; empty for a passage. */
  words: string;
  source: ProjectSearchWordSource;
  /**
   * Where the text is: `[yMin, xMin, yMax, xMax]`, 0 to 1000 on the page as displayed, origin top
   * left, as both search APIs return it. Empty when the server sent none.
   */
  box: readonly number[];
  match: ProjectSearchMatch;
  /** A passage's rank in the keyword and the meaning results, 1 best, 0 when not in that list. */
  keywordRank?: number;
  meaningRank?: number;
  /** The plan object the hit stands for, when the project is searched by its objects. */
  entity?: ProjectSearchEntity;
}

/** What the plan says about one page: its sheet, its title and the AI transcript of the page. */
export interface ProjectSearchPage {
  fileId: string;
  pageNumber: number;
  sheetId: string;
  title: string;
  /** The page's transcript in Markdown, written by the model that read the page at ingestion. */
  transcript: string;
}

/** One file of the project, with the page its Preview opens on. */
export interface ProjectSearchFile {
  fileId: string;
  name: string;
  firstPage: number;
  pages: number;
}

/** The results found on one page, best first. */
export interface ProjectSearchPageGroup {
  fileId: string;
  pageNumber: number;
  sheetId: string;
  hits: ProjectSearchHit[];
}

/** One run of a result's text, marked when it is a word of the query. */
export interface TextSegment {
  text: string;
  mark: boolean;
}

/**
 * Order of the match kinds in the panel: passages that match both the words and the meaning
 * first, meaning-only ("related") passages last. Exact matches only appear without the others.
 */
/**
 * Groups hits by page, keeping the server's order (Issue #3051): the server ranks first the
 * passages that hold the query's identifiers and more of its words, whichever search found them,
 * so the panel doesn't re-sort them by how they matched. A page takes the place of its best hit.
 */
export function groupByPage(hits: readonly ProjectSearchHit[]): ProjectSearchPageGroup[] {
  const groups = new Map<string, ProjectSearchPageGroup>();
  for (const hit of hits) {
    const key = `${hit.fileId}/${hit.pageNumber}`;
    let group = groups.get(key);
    if (!group) {
      group = { fileId: hit.fileId, pageNumber: hit.pageNumber, sheetId: hit.sheetId, hits: [] };
      groups.set(key, group);
    }
    group.hits.push(hit);
  }
  return [...groups.values()];
}

/**
 * The words of a query worth marking in the results: runs of letters or digits, two characters or
 * longer, in lower case, each once. A single character would mark nearly every line of a sheet.
 */
export function queryTerms(query: string): string[] {
  const terms = query
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(term => term.length >= 2);
  return [...new Set(terms)];
}

/** Splits `text` into runs, marking every occurrence of one of `terms`, ignoring case. */
export function markTerms(text: string, terms: readonly string[]): TextSegment[] {
  if (!text) return [];
  if (terms.length === 0) return [{ text, mark: false }];
  // Longest first, so "walls" is marked whole rather than as "wall" plus "s".
  const alternatives = [...terms].sort((first, second) => second.length - first.length).map(escapeRegExp);
  const pattern = new RegExp(alternatives.join('|'), 'giu');
  const segments: TextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > last) segments.push({ text: text.slice(last, start), mark: false });
    segments.push({ text: match[0], mark: true });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), mark: false });
  return segments;
}

/** `text` with every character that means something in a regular expression escaped. */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Space left around the text in a highlight, in PDF points, so the box frames the words. */
export const HIGHLIGHT_PADDING_POINTS = 4;

/**
 * A search box as a normalized display-page rectangle. It can be put in a route before the PDF is
 * fetched; Preview converts it to points after the first page parse.
 */
export function toPermilleBox(box: readonly number[]): HighlightBox | null {
  if (box.length !== 4) return null;
  const [yMin, xMin, yMax, xMax] = box;
  if (!(xMin >= 0 && yMin >= 0 && xMax <= 1000 && yMax <= 1000 && xMax > xMin && yMax > yMin)) return null;
  return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
}

/**
 * A hit's box in the unit of Preview highlight links: PDF points on the page as displayed (see
 * `highlight-regions.ts`), for a page that is `width` by `height` points as displayed. The box is
 * padded a little and kept on the page. Returns `null` when the box is missing or malformed.
 */
export function toPageBox(box: readonly number[], width: number, height: number): HighlightBox | null {
  const normalized = toPermilleBox(box);
  if (!normalized || !(width > 0) || !(height > 0)) return null;
  const left = Math.max(0, (normalized.x / 1000) * width - HIGHLIGHT_PADDING_POINTS);
  const top = Math.max(0, (normalized.y / 1000) * height - HIGHLIGHT_PADDING_POINTS);
  const right = Math.min(width, ((normalized.x + normalized.width) / 1000) * width + HIGHLIGHT_PADDING_POINTS);
  const bottom = Math.min(height, ((normalized.y + normalized.height) / 1000) * height + HIGHLIGHT_PADDING_POINTS);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * A sheet ID worth showing in a heading: one short token, not a placeholder such as "N/A".
 * Stored IDs are sometimes a model's whole sentence or a placeholder (#2693), which would bury
 * the page number.
 */
export function displaySheetId(sheetId: string): string {
  const id = sheetId.trim();
  if (!id || id.length > 16 || /\s/.test(id) || /^(n\/?a|none|unknown|null|-+)$/i.test(id)) return '';
  return id;
}

/** How each kind of plan object is named in a result. */
export const ENTITY_KIND_LABELS: Readonly<Record<string, string>> = {
  sheet: 'Sheet',
  drawing: 'Drawing',
  unit: 'Unit',
  room: 'Room',
  fixture: 'Fixture',
  'note-block': 'Notes',
  schedule: 'Schedule',
  detail: 'Detail',
};

/** A plan object's kind and name, such as "Room · Bath 1": the label of its Preview highlight. */
export function entityName(entity: ProjectSearchEntity): string {
  const kind = ENTITY_KIND_LABELS[entity.kind] ?? entity.kind.replace(/-/g, ' ');
  return `${kind} · ${entity.title}`;
}

/**
 * A plan object as a result reads (Issue #3212): its kind and name, then what holds it and its
 * sheet, such as "Room · Bath 1 — Unit Type D, A1.7". A parent named like the object is left out,
 * as is a sheet ID not worth showing (`displaySheetId`).
 */
export function entityLabel(entity: ProjectSearchEntity, sheetId: string): string {
  const parent = entity.parentTitle.trim();
  const where = [parent && parent !== entity.title.trim() ? parent : '', displaySheetId(sheetId)].filter(part => part);
  return where.length > 0 ? `${entityName(entity)} — ${where.join(', ')}` : entityName(entity);
}

/** The Preview route of a page: the same `/projects/:id/files/:fileId/pages/:page/preview` as chat links. */
export function previewCommands(projectId: string, fileId: string, pageNumber: number): string[] {
  return ['/projects', projectId, 'files', fileId || '1', 'pages', String(pageNumber), 'preview'];
}

/** How current the project's passage index is for the pages a search covered (Issue #2900). */
export type ProjectSearchIndexState = 'current' | 'partial' | 'outdated' | 'unknown';

/**
 * What the panel says about the index, or `null` when there is nothing to say. Nothing rebuilds
 * an index on its own yet (#2900), so the wording states what the index covers and does not
 * promise that an update is under way.
 */
export function indexNotice(state: ProjectSearchIndexState, pagesIndexed: number, pagesTotal: number): string | null {
  if (state === 'partial' && pagesTotal > 0) {
    return `The search index covers ${pagesIndexed} of ${pagesTotal} pages. Pages not in it were not searched.`;
  }
  if (state === 'outdated') {
    return "This project's search index is out of date, so results may miss recent changes.";
  }
  return null;
}

/** Longest excerpt shown from one transcript line, in characters. */
export const MAX_EXCERPT_CHARACTERS = 240;

/**
 * The line of a page's transcript that says what a hit says, to show in place of the hit's own
 * text, or `null` when the transcript has no such line.
 *
 * A hit's text is read from the sheet: on a scanned sheet that is text recognition, which reads
 * title blocks and dimension strings as noise ("t dt kr 1/4 ... unit type d1 ned"). The model that
 * transcribed the page at ingestion read the same sheet as a person would ("Drawing Title: UNIT
 * TYPE D1 ELECTRICAL"). The transcript has no word positions, so the highlight still comes from
 * the hit; only the text shown changes.
 *
 * A line qualifies when it holds the hit's matched words as a phrase, or names every word of the
 * query (see `namesQuery`): recognition misreads ("UNIT TYPE DI" for D1) keep the phrase from the
 * transcript, so the query is the fallback. A line holding the phrase comes first; among equals,
 * the line sharing the most words with the hit's own text wins, so a page that names "Unit Type
 * D" in several places shows the one the hit is on.
 *
 * A passage that matched by meaning alone keeps its own text (#3043). It need not hold any word of
 * the query, so a line that names the query would show words the passage doesn't have: searching
 * "Unit 108" showed a passage reading "UNIT 105" as the roof plan's list naming "UNIT 108".
 */
export function transcriptExcerpt(transcript: string, hit: ProjectSearchHit, query: string): string | null {
  if (!transcript || wordsOf(query).length === 0 || hit.match === 'meaning') return null;
  const phrase = normalizeWords(hit.words);
  const hitWords = new Set(wordsOf(hit.text).filter(word => word.length >= 3));
  let best: { line: string; score: number } | null = null;
  for (const line of transcriptLines(transcript)) {
    const holdsPhrase = phrase !== '' && ` ${normalizeWords(line)} `.includes(` ${phrase} `);
    if (!holdsPhrase && !namesQuery(line, query)) continue;
    const overlap = new Set(wordsOf(line).filter(word => hitWords.has(word))).size;
    const score = (holdsPhrase ? 1000 : 0) + overlap;
    if (!best || score > best.score) best = { line, score };
  }
  return best ? excerptAround(best.line, query) : null;
}

/**
 * A transcript's lines as plain sentences: Markdown headings, list markers, emphasis and code
 * marks removed, table cells joined with " · ", and table rules and blank lines left out.
 */
export function transcriptLines(transcript: string): string[] {
  const lines: string[] = [];
  for (const raw of transcript.split(/\r?\n/)) {
    let line = raw.trim();
    if (/^\|?\s*:?-{3,}/.test(line)) continue;
    if (line.startsWith('|')) {
      line = line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell)
        .join(' · ');
    }
    line = line
      .replace(/^#{1,6}\s+/, '')
      .replace(/^>\s*/, '')
      .replace(/^(?:[-*+]|\d+[.)])\s+/, '')
      .replace(/\*\*|__|`/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (line) lines.push(line);
  }
  return lines;
}

/**
 * At most this much of a line is kept before the match, so the match is within the three lines a
 * row shows even in a narrow panel (#2991).
 */
const EXCERPT_LEAD_CHARACTERS = 40;

/**
 * A window of `line` that starts shortly before the query's match and holds at most
 * `MAX_EXCERPT_CHARACTERS`, cut at spaces and marked with "…" where it is cut.
 */
function excerptAround(line: string, query: string): string {
  const at = matchStart(line, query);
  let start = at > EXCERPT_LEAD_CHARACTERS ? at - EXCERPT_LEAD_CHARACTERS : 0;
  if (start > 0) {
    const space = line.indexOf(' ', start);
    start = space >= 0 && space < at ? space + 1 : at;
  }
  let end = Math.min(line.length, start + MAX_EXCERPT_CHARACTERS);
  if (end < line.length) end = line.lastIndexOf(' ', end) > start ? line.lastIndexOf(' ', end) : end;
  return `${start > 0 ? '…' : ''}${line.slice(start, end).trim()}${end < line.length ? '…' : ''}`;
}

/**
 * Where `line` first holds the query's words in order as a phrase ("UNIT 108"), each word possibly
 * the start of a longer one as in `namesQuery` ("UNITS 108"), or else its first use of the query
 * word it uses least: in a list of units, "108" and not "unit".
 */
function matchStart(line: string, query: string): number {
  // The words are runs of letters and digits only, so they need no escaping in a pattern.
  const words = wordsOf(query);
  const gap = '[\\p{L}\\p{N}]*[^\\p{L}\\p{N}]+';
  const phrase = new RegExp(`(?<![\\p{L}\\p{N}])${words.join(gap)}`, 'iu').exec(line);
  if (phrase) return phrase.index;
  const lower = line.toLowerCase();
  let best: { at: number; uses: number } | null = null;
  for (const word of words) {
    const at = lower.indexOf(word);
    if (at < 0) continue;
    const uses = lower.split(word).length - 1;
    if (!best || uses < best.uses) best = { at, uses };
  }
  return best?.at ?? 0;
}

/** Lower-case runs of letters and digits. */
function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(word => word);
}

/** `text`'s words joined by single spaces, for phrase comparison. */
function normalizeWords(text: string): string {
  return wordsOf(text).join(' ');
}

/** Most sheets and files listed above the text results. */
export const MAX_NAME_MATCHES = 6;

/**
 * True when every word of the query names a word of `name`. A word of two or more characters
 * matches the start of a word ("elec" finds "ELECTRICAL"); a single character matches only a word
 * that is that letter, alone or followed by digits, so "Unit Type D" finds "UNIT TYPE D1" but not
 * "UNIT TYPE A DETAILS".
 */
export function namesQuery(name: string, query: string): boolean {
  const wanted = wordsOf(query);
  if (wanted.length === 0) return false;
  const words = wordsOf(name);
  return wanted.every(term =>
    term.length >= 2
      ? words.some(word => word.startsWith(term))
      : words.some(word => word === term || new RegExp(`^${escapeRegExp(term)}\\d+$`, 'u').test(word))
  );
}

/** The sheets whose title or sheet ID names the query, in plan order. */
export function matchSheets(pages: readonly ProjectSearchPage[], query: string): ProjectSearchPage[] {
  return pages.filter(page => namesQuery(`${page.title} ${displaySheetId(page.sheetId)}`, query));
}

/** The files whose name names the query, in project order. */
export function matchFiles(files: readonly ProjectSearchFile[], query: string): ProjectSearchFile[] {
  return files.filter(file => namesQuery(file.name, query));
}
